import React, { useEffect, useId, useRef, useState } from 'react';

import { Input } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import NumericInputField from 'components/common/NumericInputField';

import GridVoiceMic from './GridVoiceMic';

// Inputs that sit in the Paddocks grid ready to type into — no edit mode.
// They look like plain text until hovered or focused (.paddocks-grid-field,
// App.css), save on Enter or leaving the field, and Escape puts the saved
// value back (OP-2481). `column` tags the field so Up and Down move to the
// same field in the next row (PaddocksView).

/**
 * A field's error as the Tasks grid shows it (EditableCell): a small red
 * callout floating under the cell, so the row doesn't grow taller.
 */
export const GridFieldError: React.FC<{ message: string }> = ({
  message,
}) => (
  <div
    role="alert"
    className="absolute left-0 top-full z-20 mt-2 whitespace-nowrap rounded bg-danger-light px-2 py-1 text-xs font-semibold text-danger shadow"
  >
    {message}
    <span className="absolute -top-1 left-2 h-2 w-2 rotate-45 bg-danger-light" />
  </div>
);

interface GridTextInputProps {
  value: string;
  ariaLabel: string;
  column: string;
  /**
   * Shown under the field when it's left empty; the saved value returns.
   * Omit for an optional field (notes), which can be cleared.
   */
  requiredMessage?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  /**
   * Wraps and grows with its text (notes) — one line at rest, up to six.
   * Shift+Enter starts a new line; Enter still saves.
   */
  multiline?: boolean;
  onCommit: (value: string) => void;
  /** Leaving the field empty or pressing Escape — used by the add row. */
  onCancel?: () => void;
}

export const GridTextInput: React.FC<GridTextInputProps> = ({
  value,
  ariaLabel,
  column,
  requiredMessage,
  placeholder,
  className,
  autoFocus,
  multiline,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const skipCommitRef = useRef(false);
  const textAreaRef = useRef<TextAreaRef>(null);

  // A save or another device can change the value while it isn't focused.
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      return;
    }
    const trimmed = draft.trim();
    if (!trimmed && requiredMessage) {
      setDraft(value);
      if (onCancel) {
        onCancel();
      } else {
        setError(requiredMessage);
      }
      return;
    }
    setError(null);
    if (trimmed !== value) onCommit(trimmed);
  };

  const fieldProps = {
    value: draft,
    placeholder,
    'aria-label': ariaLabel,
    'aria-invalid': !!error,
    autoFocus,
    status: error ? ('error' as const) : undefined,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setDraft(e.target.value);
      if (error) setError(null);
    },
    onBlur: commit,
    onKeyDown: (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (e.key === 'Enter' && !(multiline && e.shiftKey)) {
        e.preventDefault();
        e.currentTarget.blur();
      } else if (e.key === 'Escape') {
        skipCommitRef.current = true;
        setDraft(value);
        setError(null);
        onCancel?.();
        e.currentTarget.blur();
      }
    },
  };

  return (
    <div
      className={`paddocks-grid-field relative ${className ?? ''}`}
      data-grid-col={column}
      // Unsaved typing keeps Ctrl/⌘+Z for the field (usePaddocksUndo).
      data-dirty={draft !== value}
    >
      {multiline ? (
        // Not TextAreaField: its two-row minimum and voice bar don't fit a
        // grid row, so the grid keeps its own one-line, growing textarea.
        <>
          <Input.TextArea
            {...fieldProps}
            ref={textAreaRef}
            autoSize={{ minRows: 1, maxRows: 6 }}
          />
          <GridVoiceMic
            textAreaRef={textAreaRef}
            text={draft}
            onText={setDraft}
          />
        </>
      ) : (
        <Input {...fieldProps} />
      )}
      {error && <GridFieldError message={error} />}
    </div>
  );
};

interface GridNumberInputProps {
  value: number | null;
  ariaLabel: string;
  column: string;
  onCommit: (value: number | null) => void;
}

export const GridNumberInput: React.FC<GridNumberInputProps> = ({
  value,
  ariaLabel,
  column,
  onCommit,
}) => {
  const [draft, setDraft] = useState<number | null>(value);
  const skipCommitRef = useRef(false);
  // NumericInputField takes an id, not an aria-label, so a hidden label names it.
  const id = useId();

  useEffect(() => setDraft(value), [value]);

  return (
    <div
      className="paddocks-grid-field"
      data-grid-col={column}
      data-dirty={draft !== value}
    >
      <label htmlFor={id} className="sr-only">
        {ariaLabel}
      </label>
      <NumericInputField
        id={id}
        value={draft}
        min={0}
        precision={2}
        controls={false}
        keyboard={false}
        size="middle"
        inputClassName="w-full"
        onChange={setDraft}
        onBlur={() => {
          if (skipCommitRef.current) {
            skipCommitRef.current = false;
            return;
          }
          if (draft !== value) onCommit(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            skipCommitRef.current = true;
            setDraft(value);
            e.currentTarget.blur();
          }
        }}
      />
    </div>
  );
};
